import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  eyebrow?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  eyebrow,
  children,
  footer
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);
  return createPortal(<AnimatePresence data-id="element-573" data-mp="3ae5ir1ptb5l">
      {open ? <div className="fixed inset-0 z-50 flex items-end justify-end sm:items-stretch" data-id="element-574" data-mp="39crcdpek8jn">
          <motion.div className="absolute inset-0 bg-navy-900/45" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} onClick={onClose} aria-hidden="true" data-id="element-575" data-mp="897hug59mw46" />
          <motion.aside ref={panelRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} initial={{
        opacity: 0,
        y: 32
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 24
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="relative flex max-h-[88vh] w-full flex-col rounded-t-2xl bg-white shadow-panel outline-none sm:max-h-none sm:h-full sm:w-[30rem] sm:rounded-none" data-id="element-576" data-mp="3gqc615w5xzhm">
            <div className="flex items-start gap-4 border-b border-navy-100 px-5 py-4" data-id="element-577" data-mp="2pwa9h2ftov80">
              <div className="min-w-0 flex-1" data-id="element-578" data-mp="2kyh4udu6499u">
                {eyebrow ? <p className="text-xs font-semibold uppercase tracking-wide text-brand-600" data-id="element-579" data-mp="2zmvb6yqsmfv5">
                    {eyebrow}
                  </p> : null}
                <h2 className="mt-0.5 text-base font-semibold text-navy-900" data-id="element-580" data-mp="26ypmk0wmc6q1">
                  {title}
                </h2>
                {subtitle ? <p className="mt-1 text-sm text-navy-500" data-id="element-581" data-mp="221zarprujxaf">{subtitle}</p> : null}
              </div>
              <button type="button" onClick={onClose} aria-label="Close panel" className="-mr-1 -mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg text-navy-400 hover:bg-navy-50 hover:text-navy-700" data-id="element-582" data-mp="2gv2h10zzmolu">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-583" data-mp="3nftukkc1vc98" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-584" data-mp="3uzcvw6p5vs6m">{children}</div>
            {footer ? <div className="border-t border-navy-100 px-5 py-4" data-id="element-585" data-mp="3lwbrcafy2952">{footer}</div> : null}
          </motion.aside>
        </div> : null}
    </AnimatePresence>, document.body);
}